import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type AdminRole = 'PlatformAdmin' | 'SupportAgent';

export interface AdminUser {
  userId: string;
  givenName: string;
  familyName: string;
  email: string;
  role: AdminRole;
  team: string;
  lastLoginAt: string;
}

export const ROLE_LABEL: Record<AdminRole, string> = {
  PlatformAdmin: 'Platform Admin',
  SupportAgent: 'Support Agent (AP L1)',
};

export type AdminCapability =
  | 'tenants.read'
  | 'tenants.suspend'
  | 'tenants.terminate'
  | 'invoices.read.cross_tenant'
  | 'rules.edit'
  | 'rules.read'
  | 'audit.read'
  | 'audit.export'
  | 'monitoring.read'
  | 'tickstar.read'
  | 'tickstar.requeue'
  | 'certificates.read'
  | 'certificates.rotate'
  | 'reports.generate'
  | 'reports.read'
  | 'settings.write';

const ROLE_CAPS: Record<AdminRole, Set<AdminCapability>> = {
  PlatformAdmin: new Set([
    'tenants.read',
    'tenants.suspend',
    'tenants.terminate',
    'invoices.read.cross_tenant',
    'rules.edit',
    'rules.read',
    'audit.read',
    'audit.export',
    'monitoring.read',
    'tickstar.read',
    'tickstar.requeue',
    'certificates.read',
    'certificates.rotate',
    'reports.generate',
    'reports.read',
    'settings.write',
  ]),
  SupportAgent: new Set([
    'tenants.read',
    'invoices.read.cross_tenant',
    'rules.read',
    'audit.read',
    'monitoring.read',
    'tickstar.read',
    'reports.read',
  ]),
};

const USERS: AdminUser[] = [
  {
    userId: 'admin-001',
    givenName: 'Lóránd',
    familyName: 'Galambos',
    email: 'platform-admin@solara.ae',
    role: 'PlatformAdmin',
    team: 'ZIP2000 Platform Operations',
    lastLoginAt: new Date(Date.now() - 2 * 3600_000).toISOString(),
  },
  {
    userId: 'admin-002',
    givenName: 'Layla',
    familyName: 'Hassan',
    email: 'support@solara.ae',
    role: 'SupportAgent',
    team: 'Alliance Prime · Dubai L1 Support',
    lastLoginAt: new Date(Date.now() - 25 * 60_000).toISOString(),
  },
];

const STORAGE_KEY = 'solara.admin.userId';

interface AuthCtx {
  users: AdminUser[];
  currentUser: AdminUser;
  setRole: (role: AdminRole) => void;
  setUser: (id: string) => void;
  can: (cap: AdminCapability) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string>(() => {
    if (typeof window === 'undefined') return USERS[0]!.userId;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && USERS.some((u) => u.userId === stored)) return stored;
    return USERS[0]!.userId;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, userId);
    }
  }, [userId]);

  const currentUser = useMemo(
    () => USERS.find((u) => u.userId === userId) ?? USERS[0]!,
    [userId],
  );

  const setUser = useCallback((id: string) => setUserId(id), []);
  const setRole = useCallback((role: AdminRole) => {
    const target = USERS.find((u) => u.role === role) ?? USERS[0]!;
    setUserId(target.userId);
  }, []);
  const can = useCallback(
    (cap: AdminCapability) => ROLE_CAPS[currentUser.role].has(cap),
    [currentUser.role],
  );

  const value = useMemo<AuthCtx>(
    () => ({ users: USERS, currentUser, setRole, setUser, can }),
    [currentUser, setRole, setUser, can],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth must be used inside <AuthProvider>');
  return c;
}

export function useCan(cap: AdminCapability): boolean {
  return useAuth().can(cap);
}
